/**
 * NER-1281: pure theme helpers (auto / light / dark).
 *
 * Extraido aparte de ThemeContext para poder testear la logica sin React ni
 * imports que arrastren axios (mismo patron que deliveryPlatforms.js /
 * paymentMethods.js — el repo no tiene @testing-library).
 *
 * Preferencia ternaria persistida: 'light' | 'dark' | 'auto' (default 'auto').
 * El tema RESUELTO (lo que se pinta) siempre es 'light' | 'dark'.
 */

// Clave logica nueva — el storage util la prefija como `admin_theme-preference`.
export const PREFERENCE_KEY = 'theme-preference';

// Clave legacy binaria (light|dark) que escribia la version anterior.
export const LEGACY_KEY = 'digital-menu-theme';

// Orden del cycle del toggle: light -> dark -> auto -> light.
export const THEME_PREFERENCES = ['light', 'dark', 'auto'];

export const DEFAULT_PREFERENCE = 'auto';

// --- P2 (NER-1564): sub-modo de 'auto' --------------------------------------
// Cuando la preferencia es 'auto', el sub-modo decide COMO se resuelve:
//   'system'      -> sigue prefers-color-scheme del navegador (default).
//   'time-of-day' -> light entre [7:00, 19:00) local, dark fuera de esa ventana.
export const AUTO_MODE_KEY = 'theme-auto-mode';
export const AUTO_MODES = ['system', 'time-of-day'];
export const DEFAULT_AUTO_MODE = 'system';

// Ventana diurna [DAY_START_HOUR, DAY_END_HOUR) en hora local del browser.
export const DAY_START_HOUR = 7;
export const DAY_END_HOUR = 19;

export function isValidPreference(value) {
  return THEME_PREFERENCES.includes(value);
}

export function isValidAutoMode(value) {
  return AUTO_MODES.includes(value);
}

/**
 * Sub-modo de 'auto' inicial: el guardado valido gana, si no default 'system'.
 * Puro (no toca storage).
 */
export function resolveInitialAutoMode(savedAutoMode) {
  return isValidAutoMode(savedAutoMode) ? savedAutoMode : DEFAULT_AUTO_MODE;
}

/**
 * True si `hour` cae en la ventana diurna [7:00, 19:00).
 * Si no se pasa una hora valida, usa la hora local actual del browser.
 */
export function isDaytime(hour) {
  const h = Number.isInteger(hour) ? hour : new Date().getHours();
  return h >= DAY_START_HOUR && h < DAY_END_HOUR;
}

/**
 * Siguiente preferencia en el cycle light -> dark -> auto -> light.
 */
export function nextPreference(current) {
  const idx = THEME_PREFERENCES.indexOf(current);
  if (idx === -1) return DEFAULT_PREFERENCE;
  return THEME_PREFERENCES[(idx + 1) % THEME_PREFERENCES.length];
}

/**
 * Resuelve la preferencia a un tema concreto a aplicar.
 * Cuando es 'auto', el sub-modo decide la fuente:
 *   - 'system'      -> flag del sistema (prefers-color-scheme: dark).
 *   - 'time-of-day' -> hora local (light de dia, dark de noche).
 * `options` es opcional y backward-compatible: sin opciones se comporta como
 * 'system' (firma vieja `resolveTheme(pref, systemPrefersDark)` intacta).
 */
export function resolveTheme(preference, systemPrefersDark, options = {}) {
  if (preference === 'dark') return 'dark';
  if (preference === 'light') return 'light';
  // preference === 'auto'
  const { autoMode = DEFAULT_AUTO_MODE, hour } = options;
  if (autoMode === 'time-of-day') {
    return isDaytime(hour) ? 'light' : 'dark';
  }
  return systemPrefersDark ? 'dark' : 'light';
}

/**
 * Decide la preferencia inicial a partir de lo guardado.
 * - Si la clave nueva es valida, gana.
 * - Si no, migra desde la clave legacy binaria (light|dark).
 * - Si nada aplica, default 'auto'.
 * Retorna { preference, migratedFromLegacy } sin tocar el storage (puro).
 */
export function resolveInitialPreference(savedPreference, legacyValue) {
  if (isValidPreference(savedPreference)) {
    return { preference: savedPreference, migratedFromLegacy: false };
  }
  if (legacyValue === 'light' || legacyValue === 'dark') {
    return { preference: legacyValue, migratedFromLegacy: true };
  }
  return { preference: DEFAULT_PREFERENCE, migratedFromLegacy: false };
}
